import { cva } from 'class-variance-authority';

/**
 * Button class recipe.
 *
 * Deliberately contains ZERO `dark:` variants. Colour comes entirely from the `--ds-btn-*` slot
 * variables, which the `.ds-btn-<appearance>-<fill>` classes in `components/styles/design-system.css`
 * set per theme. One class list therefore serves both themes.
 *
 * Sizing is height-based rather than padding-based on purpose: Figma gives each size a fixed height
 * (32/36/40/44), and `fill="outline"` adds a border. With padding-based sizing that border would
 * make outline buttons 2-3px taller than their solid siblings; with a fixed height plus the app's
 * global `box-sizing: border-box` they line up exactly.
 */
export const buttonRecipe = cva(
  [
    'group/btn',
    'relative inline-flex shrink-0 items-center justify-center',
    'rounded-full border-solid',
    'font-sans leading-5 font-medium whitespace-nowrap',
    'cursor-pointer select-none',
    'transition-colors duration-150 motion-reduce:transition-none',

    // Slot reads. The `.ds-btn-*` class supplies the values.
    'border-[length:var(--ds-btn-border-width)] border-[var(--ds-btn-border)]',
    'bg-[var(--ds-btn-bg)] text-[var(--ds-btn-fg)]',

    // Interactive states. The `:not([data-disabled])` guard makes these selectors disjoint from the
    // disabled ones below, so which rule wins never depends on utility sort order.
    '[&:not([data-disabled]):hover]:bg-[var(--ds-btn-bg-hover)]',
    '[&:not([data-disabled]):hover]:text-[var(--ds-btn-fg-hover)]',
    '[&:not([data-disabled]):active]:bg-[var(--ds-btn-bg-pressed)]',

    // Base UI sets `data-disabled` for both a native `disabled` and the `focusableWhenDisabled`
    // (aria-disabled) case, so keying off it covers both.
    '[&[data-disabled]]:cursor-not-allowed',
    '[&[data-disabled]]:bg-[var(--ds-btn-bg-disabled)]',
    '[&[data-disabled]]:text-[var(--ds-btn-fg-disabled)]',
    '[&[data-disabled]]:border-[var(--ds-btn-border-disabled)]',

    // The Figma matrix has NO focus state. This is added for WCAG 2.4.7, using the focus treatment
    // already specified in Natter's DESIGN.md (2px primary outline, 2px offset).
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-primary',
  ],
  {
    variants: {
      size: {
        xs: 'text-[12px] [--ds-btn-icon-size:16px]',
        small: 'text-[13px] [--ds-btn-icon-size:18px]',
        regular: 'text-[13px] [--ds-btn-icon-size:20px]',
        large: 'text-[14px] [--ds-btn-icon-size:20px]',
      },
      /** Figma's `Style=Icon` — a square button with a single centred icon and no label. */
      iconOnly: {
        true: 'gap-0 p-0',
        false: 'gap-1',
      },
      /**
       * Busy. NOT in the Figma matrix, which has no loading cell (flagged to design, HNS-263) — the
       * treatment below is built from existing tokens rather than invented colours, so it inherits
       * every appearance and both themes for free.
       *
       * A loading button is inert, so it carries `data-disabled` and would otherwise pick up the
       * washed-out disabled tokens. Remapping each disabled slot back onto its normal counterpart
       * keeps the button looking like ITSELF while busy: "working", not "switched off". This is
       * exactly what the slot indirection is for — no new colour is introduced.
       */
      loading: {
        true: [
          '[--ds-btn-bg-disabled:var(--ds-btn-bg)]',
          '[--ds-btn-fg-disabled:var(--ds-btn-fg)]',
          '[--ds-btn-border-disabled:var(--ds-btn-border)]',
          '[--ds-btn-disabled-opacity:1]',
          '[&[data-disabled]]:cursor-progress',
        ],
        false: '',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      appearance: {
        primary: '',
        secondary: '',
        success: '',
        destructive: '',
        muted: '',
      },
      fill: {
        default: '',
        outline: '',
        ghost: '',
        subtle: '',
      },
    },
    compoundVariants: [
      // Height + horizontal padding. Icon-only is square, so it takes no padding.
      { size: 'xs', iconOnly: false, class: 'h-8 px-4' },
      { size: 'small', iconOnly: false, class: 'h-9 px-5' },
      { size: 'regular', iconOnly: false, class: 'h-10 px-5' },
      { size: 'large', iconOnly: false, class: 'h-11 px-5' },
      { size: 'xs', iconOnly: true, class: 'size-8' },
      { size: 'small', iconOnly: true, class: 'size-9' },
      { size: 'regular', iconOnly: true, class: 'size-10' },
      { size: 'large', iconOnly: true, class: 'size-11' },

      // Appearance x fill -> the slot-setting class. `ghost` is muted-only in the design and
      // `subtle` destructive-only, both enforced by the Props type; there is intentionally no
      // primary/secondary/success/destructive ghost, and no subtle for anything else.
      { appearance: 'primary', fill: 'default', class: 'ds-btn-primary-default' },
      { appearance: 'primary', fill: 'outline', class: 'ds-btn-primary-outline' },
      { appearance: 'secondary', fill: 'default', class: 'ds-btn-secondary-default' },
      { appearance: 'secondary', fill: 'outline', class: 'ds-btn-secondary-outline' },
      { appearance: 'success', fill: 'default', class: 'ds-btn-success-default' },
      { appearance: 'success', fill: 'outline', class: 'ds-btn-success-outline' },
      { appearance: 'destructive', fill: 'default', class: 'ds-btn-destructive-default' },
      { appearance: 'destructive', fill: 'outline', class: 'ds-btn-destructive-outline' },
      { appearance: 'destructive', fill: 'subtle', class: 'ds-btn-destructive-subtle' },
      { appearance: 'muted', fill: 'default', class: 'ds-btn-muted-default' },
      { appearance: 'muted', fill: 'outline', class: 'ds-btn-muted-outline' },
      { appearance: 'muted', fill: 'ghost', class: 'ds-btn-muted-ghost' },
    ],
    defaultVariants: {
      appearance: 'primary',
      fill: 'default',
      size: 'regular',
      iconOnly: false,
      fullWidth: false,
      loading: false,
    },
  }
);

/**
 * Wrapper around the label and icons.
 *
 * This element exists because the disabled treatment for `default` and `ghost` fills is an opacity
 * on the CONTENT only — the background keeps its full-strength disabled token. Applying the opacity
 * to the button itself would wash the background out too. `outline` fills set the slot to 1, so the
 * same markup covers all three fills.
 */
export const buttonContentRecipe = cva(
  [
    'inline-flex items-center justify-center gap-[inherit]',
    'group-data-[disabled]/btn:opacity-[var(--ds-btn-disabled-opacity)]',
  ],
  {
    variants: {
      /**
       * Hidden with `opacity-0`, NOT unmounted, and deliberately so:
       *
       *   - the label keeps reserving its width, so the button does not shrink the moment it starts
       *     working. A button that collapses mid-submit moves everything beside it.
       *   - the accessible name survives. Removing the text would leave a screen-reader user on an
       *     unnamed button precisely when something is happening.
       */
      loading: { true: 'opacity-0', false: '' },
    },
    defaultVariants: { loading: false },
  }
);

/**
 * The busy indicator, centred over the hidden label.
 *
 * A bordered circle with one transparent edge rather than an SVG or a new token: `border-current`
 * resolves to `--ds-btn-fg`, so it is automatically the right colour in every appearance, both
 * fills and both themes. Sized off `--ds-btn-icon-size` so it tracks the size scale.
 */
export const buttonSpinnerRecipe = cva([
  'pointer-events-none absolute inset-0 m-auto',
  'size-[var(--ds-btn-icon-size)] rounded-full',
  'border-2 border-current border-t-transparent',
  'animate-spin motion-reduce:animate-none',
]);

/** Sizes any icon passed via `startIcon` / `endIcon` / `children` of an icon-only button. */
export const buttonIconRecipe = cva([
  'inline-flex shrink-0 items-center justify-center',
  'size-[var(--ds-btn-icon-size)]',
  '[&_svg]:size-full',
]);
